import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  LinearProgress,
  TableContainer,
  TableSortLabel
} from '@material-ui/core';
import {
  AssignmentReturned as AssignmentReturnedIcon,
  Close as CloseIcon
} from '@material-ui/icons';

import { ApiParams } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import DesignOutputAssignDesignInputDialog from '../dialogs/DesignOutputAssignDesignInputDialog';
import UnlinkConfirmDialog from '../dialogs/UnlinkConfirmDialog';
import {
  useFindProductDesignInputsByDesignOutputId,
  useProductEntityPrefix
} from '../Product.hooks';
import {
  ProductDesignInput,
  BaseProductDesignOutputCardProps
} from '../Product.interface';
import { getProductEntityName } from '../Product.config';

const DesignOutputDesignInputReferenceCard: React.FC<BaseProductDesignOutputCardProps> =
  (props: BaseProductDesignOutputCardProps) => {
    const {
      productId,
      productDesignOutput,
      productDesignOutputVersion,
      onUpdate,
      onError
    } = props;
    const history = useHistory();
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [itemToUnlink, setItemToUnlink] = useState<number | undefined>();
    const [order, setOrder] = useState<string | undefined>();
    const [orderDirection, setOrderDirection] = useState<
      'ASC' | 'DESC' | undefined
    >();
    const {
      productDesignInputList,
      isLoadingProductDesignInputs,
      loadingProductDesignInputsError,
      findProductDesignInputs
    } = useFindProductDesignInputsByDesignOutputId(
      productId,
      productDesignOutput.id
    );
    const { isFieldDisabled } = useTaskEntityFields(productDesignOutput.task);
    const prefix: string = useProductEntityPrefix(
      ProductBoardColumnEntity.DesignInput
    );
    const handleSort = (column: string) => {
      setOrder(column);
      setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
    };

    const getSortDirection = (column: string): 'asc' | 'desc' => {
      return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
    };

    const findProductDesignInputsWithParams = useCallback(() => {
      const params: ApiParams = {};
      if (order) params.order = order;
      if (orderDirection) params.direction = orderDirection;
      if (productDesignOutputVersion)
        params.version = productDesignOutputVersion.version;
      findProductDesignInputs(params);
    }, [
      findProductDesignInputs,
      order,
      orderDirection,
      productDesignOutputVersion
    ]);

    useEffect(() => {
      findProductDesignInputsWithParams();
    }, [findProductDesignInputsWithParams]);

    useEffect(() => {
      if (loadingProductDesignInputsError) {
        onError?.(loadingProductDesignInputsError);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingProductDesignInputsError]);
    const disabled =
      Boolean(productDesignOutputVersion) ||
      isFieldDisabled('assignDesignInput');
    return (
      <Card>
        <CardTitle>
          <AssignmentReturnedIcon />
          {getProductEntityName('designInput')} Reference
        </CardTitle>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={order === 'productDesignInput.id'}
                    direction={getSortDirection('productDesignInput.id')}
                    onClick={() => handleSort('productDesignInput.id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'productDesignInput.title'}
                    direction={getSortDirection('productDesignInput.title')}
                    onClick={() => handleSort('productDesignInput.title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'task.status'}
                    direction={getSortDirection('task.status')}
                    onClick={() => handleSort('task.status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingProductDesignInputs && (
                <TableRow>
                  <TableCell colSpan={2} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {productDesignInputList?.map(
                (designInput: ProductDesignInput) => (
                  <TableRow
                    hover
                    key={designInput.id}
                    onClick={() => {
                      history.push(
                        `/portal/products/${productId}/design-inputs/${designInput.id}`
                      );
                    }}
                  >
                    <TableCell>{prefix + designInput.id}</TableCell>
                    <WrappedTableCell>{designInput.title}</WrappedTableCell>
                    <TableCell>
                      <TaskStatusLabel task={designInput.task} />
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: 'center'
                      }}
                    >
                      {!disabled && (
                        <CloseIcon
                          onClick={event => {
                            event.stopPropagation();
                            setItemToUnlink(designInput.id);
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                )
              )}
              <TableRow>
                <TableCell colSpan={2}>
                  <Button
                    variant='outlined'
                    color='primary'
                    disabled={disabled}
                    onClick={() => setOpenDialog(true)}
                  >
                    Assign {getProductEntityName('designInput')}
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        {openDialog && (
          <DesignOutputAssignDesignInputDialog
            open={openDialog}
            productId={productId}
            designOutputId={productDesignOutput.id}
            selectedItems={productDesignInputList}
            onError={onError}
            onCancel={() => setOpenDialog(false)}
            onAssigned={() => {
              findProductDesignInputsWithParams();
              onUpdate?.();
              setOpenDialog(false);
            }}
          />
        )}
        <UnlinkConfirmDialog
          open={!!itemToUnlink}
          productId={productId}
          entityId={productDesignOutput.id}
          from={ProductBoardColumnEntity.DesignOutput}
          to={ProductBoardColumnEntity.DesignInput}
          unlinkItemId={itemToUnlink}
          selectedItems={productDesignInputList}
          onError={onError}
          onCancel={() => setItemToUnlink(undefined)}
          onChange={() => {
            findProductDesignInputsWithParams();
            onUpdate?.();
            setItemToUnlink(undefined);
          }}
        />
      </Card>
    );
  };

export default DesignOutputDesignInputReferenceCard;
